import { BottomSheet, Text, Title, type UseModalState } from "@clipboard-health/ui-components";
import { DialogContent, Stack } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logError } from "@src/appV2/lib/analytics";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { useCallback } from "react";

import { useDeleteComment } from "../api/useDeleteComment";

interface ConfirmRemoveCommentBottomSheetProps {
  modalState: UseModalState;
  commentId: string;
  workplaceId: string;
  onRemoved: (commentId: string) => void;
}

export function ConfirmRemoveCommentBottomSheet(props: ConfirmRemoveCommentBottomSheetProps) {
  const { modalState, commentId, workplaceId, onRemoved } = props;
  const { showSuccessToast, showErrorToast } = useToast();

  const { mutate: deleteComment, isLoading: isDeletingComment } = useDeleteComment(
    { workplaceId, commentId },
    {
      onSuccess: () => {
        onRemoved(commentId);
        showSuccessToast("Your review is now removed");
        modalState.closeModal();
      },
      onError: (error) => {
        logError(APP_V2_APP_EVENTS.DELETE_COMMENT_FAILURE, {
          metadata: { commentId, workplaceId },
          error,
        });
        showErrorToast("Something went wrong while removing the review");
      },
    }
  );

  const handleClose = useCallback(() => {
    modalState.closeModal();
  }, [modalState]);

  return (
    <BottomSheet
      footer={
        <DialogFooter orientation="vertical">
          <LoadingButton
            fullWidth
            variant="contained"
            size="large"
            isLoading={isDeletingComment}
            onClick={() => {
              deleteComment();
            }}
          >
            Confirm
          </LoadingButton>
        </DialogFooter>
      }
      header={<BottomSheetHeader onClose={handleClose} />}
      modalState={modalState}
    >
      <DialogContent sx={{ backgroundColor: "unset", py: 8, mt: 8 }}>
        <Stack direction="column" alignItems="center" spacing={9}>
          <Title variant="h2" component="h2" sx={{ textAlign: "center", textWrap: "balance" }}>
            Are you sure you want to remove your review?
          </Title>

          <Text>This will remove your review from the workplace.</Text>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
